import { useState, useCallback, useEffect, useRef } from 'react'
import { clsx } from 'clsx'
import { fmtCoins } from '../../lib/cards'
import { useCountdown } from '../../hooks/useCountdown'
import type { RoundState, GameConfig, Card, ABDeal } from '../../lib/types'

const SUIT_SYMBOL: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' }
const isRed = (suit: string) => suit === 'H' || suit === 'D'

function useIsMobile() {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const fn = (e: MediaQueryListEvent) => setMobile(e.matches)
    mq.addEventListener('change', fn)
    return () => mq.removeEventListener('change', fn)
  }, [])
  return mobile
}

function MiniCard({ card, faceDown, small }: { card?: Card; faceDown?: boolean; small?: boolean }) {
  const w = small ? 28 : 34, h = small ? 40 : 48
  if (faceDown || !card) {
    return (
      <div style={{
        width: w, height: h, borderRadius: 4, flexShrink: 0,
        background: 'linear-gradient(135deg, #1a3a6e, #0f2044)',
        border: '1px solid rgba(255,255,255,0.2)',
        boxShadow: '2px 3px 8px rgba(0,0,0,0.5)',
      }} />
    )
  }
  const red = isRed(card.suit)
  return (
    <div style={{
      width: w, height: h, borderRadius: 4, background: '#fff',
      border: '1px solid #e5e7eb', flexShrink: 0,
      boxShadow: '2px 3px 8px rgba(0,0,0,0.4)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
      animation: 'mac88CardIn 0.3s ease-out both',
    }}>
      <span style={{ fontSize: small ? 11 : 13, fontWeight: 800, lineHeight: 1, color: red ? '#dc2626' : '#111827' }}>{card.rank}</span>
      <span style={{ fontSize: small ? 12 : 14, lineHeight: 1, color: red ? '#dc2626' : '#111827' }}>{SUIT_SYMBOL[card.suit]}</span>
    </div>
  )
}

function TimerBoxes({ targetMs, small }: { targetMs: number; small?: boolean }) {
  const remaining = useCountdown(targetMs)
  const d1 = Math.floor(remaining / 10)
  const d2 = remaining % 10
  const urgent = remaining <= 5
  const sz = small ? 34 : 44
  const box: React.CSSProperties = {
    width: sz, height: sz, borderRadius: 6, fontFamily: 'monospace',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: small ? 20 : 26, fontWeight: 800, color: 'white', transition: 'background 0.3s, border-color 0.3s',
    background: urgent ? '#dc2626' : '#1c2a3a',
    border: `2px solid ${urgent ? '#ef4444' : 'rgba(255,255,255,0.15)'}`,
    boxShadow: urgent ? '0 0 12px rgba(220,38,38,0.6)' : 'none',
  }
  return <div style={{ display: 'flex', gap: 4 }}><div style={box}>{d1}</div><div style={box}>{d2}</div></div>
}

interface Props {
  round:       RoundState
  config:      GameConfig
  placeBet:    (betType: string, stake: number) => Promise<{ balance: number }>
  balance:     number | null
  displayName: string
  initialResults?: string[]
}

const CHIP_VALS   = [5, 10, 25, 50, 100, 500]
const CHIP_COLORS = ['#dc2626','#2563eb','#7c3aed','#d97706','#059669','#db2777']

export function AndarBahar({ round, config, placeBet, balance, displayName, initialResults }: Props) {
  const isMobile = useIsMobile()
  const [stake,       setStake]       = useState(0)
  const [selectedChip,setSelectedChip]= useState(10)
  const [activeBet,   setActiveBet]   = useState<string | null>(null)
  const [placing,     setPlacing]     = useState(false)
  const [myBets,      setMyBets]      = useState<{ bet: string; stake: number; odds: number; outcome?: string; payout?: number }[]>([])
  const [results,     setResults]     = useState<string[]>(initialResults ?? [])
  const [toast,       setToast]       = useState<string | null>(null)
  const [showMyBets,  setShowMyBets]  = useState(false)
  const prevStatus = useRef(round.status)

useEffect(() => {
  if (initialResults && initialResults.length > 0) {
    setResults(initialResults.map(r => r.charAt(0).toUpperCase()))
  }
}, [initialResults])

  useEffect(() => {
    if (round.status === 'result' && round.result?.winner) {
      const w = (round.result as any).winner ?? '?'
      setResults(prev => [w.charAt(0).toUpperCase(), ...prev].slice(0, 12))
    }
    if (round.status === 'betting' && prevStatus.current !== 'betting') {
      setMyBets([]); setStake(0); setActiveBet(null)
    }
    if (round.status === 'result' && round.myBets.length) {
      setMyBets(round.myBets.map(b => ({
        bet: b.betType, stake: b.stake,
        odds: config.bet_types[b.betType]?.odds ?? 0,
        outcome: b.outcome, payout: b.payout,
      })))
    }
    prevStatus.current = round.status
  }, [round.status, round.myBets])

  const isBetting = round.status === 'betting'
  const deal      = round.deal as ABDeal | undefined
  const winner    = (round.result as any)?.winner

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 2500) }

  const confirmBet = useCallback(async () => {
    if (!activeBet || stake === 0 || !isBetting) return
    setPlacing(true)
    try {
      await placeBet(activeBet, stake)
      setMyBets(prev => [...prev, { bet: activeBet, stake, odds: config.bet_types[activeBet]?.odds ?? 0 }])
      showToast(`Bet placed: ${config.bet_types[activeBet]?.label} — ${fmtCoins(stake)} coins`)
      setStake(0); setActiveBet(null)
    } catch (err: any) { showToast(err.message) }
    setPlacing(false)
  }, [activeBet, stake, isBetting, placeBet, config])

  const betStake = (key: string) => myBets.filter(b => b.bet === key).reduce((s, b) => s + b.stake, 0)

  const BetCell = ({ betKey, label, highlight }: { betKey: string; label: string; highlight: string }) => (
    <button
      onClick={() => { if (isBetting) setActiveBet(betKey) }}
      disabled={!isBetting}
      style={{
        flex: 1, padding: isMobile ? '12px 6px' : '10px 8px', border: 'none', cursor: isBetting ? 'pointer' : 'default',
        background: activeBet === betKey ? `${highlight}33` : `${highlight}18`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        borderLeft: '1px solid #1e293b', transition: 'background 0.15s',
      }}>
      <span style={{ fontSize: isMobile ? 11 : 13, fontWeight: 800, color: highlight, whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ fontSize: 10, color: '#475569' }}>×{config.bet_types[betKey]?.odds ?? '?'}</span>
      {betStake(betKey) > 0 && (
        <span style={{ fontSize: 9, color: '#22c55e', fontWeight: 700 }}>{fmtCoins(betStake(betKey))}</span>
      )}
    </button>
  )

  const myBetsList = (
    <div style={{ flex: 1, overflowY: 'auto', maxHeight: isMobile ? 160 : undefined }}>
      {myBets.length === 0
        ? <div style={{ padding: '20px 12px', textAlign: 'center', fontSize: 11, color: '#334155' }}>No bets placed yet</div>
        : myBets.map((b, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 4, padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,0.04)', background: b.outcome === 'win' ? 'rgba(34,197,94,0.06)' : 'transparent', fontSize: 11 }}>
            <div>
              <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 10 }}>{config.bet_types[b.bet]?.label ?? b.bet}</div>
              {b.outcome && <div style={{ fontSize: 9, fontWeight: 700, marginTop: 2, color: b.outcome === 'win' ? '#22c55e' : b.outcome === 'push' ? '#f59e0b' : '#ef4444' }}>{b.outcome === 'win' ? `+${fmtCoins(b.payout ?? 0)}` : b.outcome.toUpperCase()}</div>}
            </div>
            <span style={{ color: '#0ea5e9', fontWeight: 700 }}>{b.odds}</span>
            <span style={{ color: '#94a3b8' }}>{fmtCoins(b.stake)}</span>
          </div>
        ))
      }
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', maxWidth: '100vw', overflowX: 'hidden', background: '#1a1f2e', color: '#e2e8f0', fontSize: 13, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '8px 10px' : '8px 14px', background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: isBetting ? '#22c55e' : '#f59e0b', boxShadow: isBetting ? '0 0 8px #22c55e' : '0 0 8px #f59e0b', animation: 'pulse 2s infinite' }} />
          <span style={{ fontWeight: 700, fontSize: isMobile ? 13 : 15, whiteSpace: 'nowrap' }}>ANDAR BAHAR</span>
          {!isMobile && <span style={{ fontSize: 10, color: '#64748b', padding: '2px 8px', background: 'rgba(255,255,255,0.05)', borderRadius: 20 }}>Rules</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {!isMobile && round.roundId && <span style={{ fontSize: 11, color: '#475569' }}>Round: {round.roundId.slice(0,12).toUpperCase()}</span>}
          <div style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 8, padding: '4px 10px', fontWeight: 700, color: '#c9a84c', fontSize: isMobile ? 12 : 14, whiteSpace: 'nowrap' }}>
            BAL {balance !== null ? `₹${fmtCoins(balance)}` : '—'}
          </div>
        </div>
      </div>

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>

          {/* Casino background */}
          <div style={{ flex: 1, position: 'relative', minHeight: isMobile ? 220 : 0, maxHeight: isMobile ? 380 : undefined, background: 'radial-gradient(ellipse 120% 80% at 60% 50%, #2d1b4e 0%, #1a0f2e 30%, #0f0820 60%, #050310 100%)', overflow: 'hidden' }}>

            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(45deg, rgba(201,168,76,0.04) 0, rgba(201,168,76,0.04) 1px, transparent 1px, transparent 24px), repeating-linear-gradient(-45deg, rgba(201,168,76,0.04) 0, rgba(201,168,76,0.04) 1px, transparent 1px, transparent 24px)', pointerEvents: 'none' }} />

            <div style={{ position: 'absolute', bottom: 0, left: isMobile ? 0 : '25%', right: 0, height: '45%', background: 'radial-gradient(ellipse 100% 80% at 50% 100%, #8b1a1a 0%, #5c1010 50%, transparent 100%)', opacity: 0.7 }} />

            {/* Joker — top-center desktop, bottom-center mobile (so piles get top row) */}
            <div style={{ position: 'absolute', ...(isMobile ? { bottom: 34, left: '50%', transform: 'translateX(-50%)' } : { top: 12, left: '50%', transform: 'translateX(-50%)' }), zIndex: 10, background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 8, padding: isMobile ? '6px 12px' : '8px 16px', backdropFilter: 'blur(4px)', textAlign: 'center' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#c9a84c', letterSpacing: 1, marginBottom: 6 }}>JOKER</div>
              {deal?.joker
                ? <MiniCard card={deal.joker} small={isMobile} />
                : <div style={{ width: isMobile ? 28 : 34, height: isMobile ? 40 : 48, borderRadius: 4, background: 'rgba(201,168,76,0.1)', border: '1px dashed rgba(201,168,76,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>?</div>
              }
            </div>

            {/* Andar pile */}
            <div style={{ position: 'absolute', top: 12, left: isMobile ? 8 : 12, zIndex: 10, background: 'rgba(0,0,0,0.7)', border: `1px solid ${winner === 'andar' ? 'rgba(34,197,94,0.5)' : 'rgba(255,255,255,0.1)'}`, borderRadius: 8, padding: isMobile ? '6px 8px' : '8px 12px', backdropFilter: 'blur(4px)', maxWidth: isMobile ? '44%' : '35%' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: winner === 'andar' ? '#22c55e' : '#94a3b8', letterSpacing: 1, marginBottom: 6 }}>
                ANDAR {winner === 'andar' && '✓ WIN'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, minHeight: isMobile ? 44 : 52 }}>
                {deal?.andar?.slice(0, isMobile ? 4 : 6).map((c: Card, i: number) => <MiniCard key={i} card={c} small={isMobile} />)}
                {!deal?.andar?.length && <div style={{ fontSize: 11, color: '#334155', paddingTop: 16 }}>cards appear here</div>}
              </div>
              {deal?.andar && deal.andar.length > (isMobile ? 4 : 6) && <div style={{ fontSize: 9, color: '#475569', marginTop: 3 }}>+{deal.andar.length - (isMobile ? 4 : 6)} more</div>}
            </div>

            {/* Bahar pile */}
            <div style={{ position: 'absolute', top: 12, right: isMobile ? 8 : '38%', zIndex: 10, background: 'rgba(0,0,0,0.7)', border: `1px solid ${winner === 'bahar' ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'}`, borderRadius: 8, padding: isMobile ? '6px 8px' : '8px 12px', backdropFilter: 'blur(4px)', maxWidth: isMobile ? '44%' : '35%' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: winner === 'bahar' ? '#ef4444' : '#94a3b8', letterSpacing: 1, marginBottom: 6 }}>
                BAHAR {winner === 'bahar' && '✓ WIN'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, minHeight: isMobile ? 44 : 52 }}>
                {deal?.bahar?.slice(0, isMobile ? 4 : 6).map((c: Card, i: number) => <MiniCard key={i} card={c} small={isMobile} />)}
                {!deal?.bahar?.length && <div style={{ fontSize: 11, color: '#334155', paddingTop: 16 }}>cards appear here</div>}
              </div>
              {deal?.bahar && deal.bahar.length > (isMobile ? 4 : 6) && <div style={{ fontSize: 9, color: '#475569', marginTop: 3 }}>+{deal.bahar.length - (isMobile ? 4 : 6)} more</div>}
            </div>

            {/* Status */}
            <div style={{ position: 'absolute', bottom: 12, left: 12, fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: isBetting ? '#22c55e' : round.status === 'result' ? '#c9a84c' : '#f59e0b', textShadow: `0 0 12px ${isBetting ? '#22c55e' : '#f59e0b'}` }}>
              {round.status === 'idle' ? 'Waiting…' : round.status === 'betting' ? '♠ Place Your Bets' : round.status === 'closed' ? 'Dealing cards…' : winner ? `${winner.toUpperCase()} WINS!` : 'Round Complete'}
            </div>

            {deal?.total_cards && (
              <div style={{ position: 'absolute', bottom: 12, right: isMobile ? 12 : '38%', fontSize: 10, color: '#475569' }}>
                Matched in {deal.total_cards} card{deal.total_cards > 1 ? 's' : ''}
              </div>
            )}

            {/* Timer — bottom-right on mobile to avoid Bahar pile */}
            {isBetting && <div style={{ position: 'absolute', ...(isMobile ? { bottom: 34, right: 8 } : { top: 12, right: 12 }) }}><TimerBoxes targetMs={round.bettingCloses} small={isMobile} /></div>}

            {round.status === 'result' && round.winAmount > 0 && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)', animation: 'mac88WinIn 0.4s ease-out', zIndex: 20 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 900, background: 'linear-gradient(90deg, #c9a84c, #f0e68c, #c9a84c)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: 2 }}>YOU WIN!</div>
                  <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: '#c9a84c', marginTop: 4 }}>+{fmtCoins(round.winAmount)} coins</div>
                </div>
              </div>
            )}
          </div>

          {/* Betting panel */}
          <div style={{ background: '#0f172a', borderTop: '2px solid #1e293b', flexShrink: 0, marginTop: 'auto' }}>

            <div style={{ display: 'flex', borderBottom: '1px solid #1e293b' }}>
              {!isMobile && <div style={{ flex: 1, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#94a3b8', display: 'flex', alignItems: 'center' }}>Andar Bahar</div>}
              <BetCell betKey="andar" label="ANDAR" highlight="#22c55e" />
              <BetCell betKey="bahar" label="BAHAR" highlight="#ef4444" />
            </div>

            <div style={{ display: 'flex', borderBottom: '1px solid #1e293b' }}>
              {!isMobile && <div style={{ flex: 1, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#94a3b8', display: 'flex', alignItems: 'center' }}>Early (1-5 cards)</div>}
              <BetCell betKey="andar_early" label={isMobile ? 'A. EARLY' : 'ANDAR EARLY'} highlight="#f59e0b" />
              <BetCell betKey="bahar_early" label={isMobile ? 'B. EARLY' : 'BAHAR EARLY'} highlight="#f59e0b" />
            </div>

            {activeBet && isBetting && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #1e293b', background: 'rgba(59,130,246,0.05)', animation: 'mac88SlideIn 0.2s ease-out', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{config.bet_types[activeBet]?.label}:</span>
                <div style={{ display: 'flex', gap: isMobile ? 6 : 4, flex: 1, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: isMobile ? 2 : 0 }}>
                  {CHIP_VALS.map((v, i) => (
                    <button key={v} onClick={() => { setSelectedChip(v); setStake(s => s + v) }}
                      style={{ width: isMobile ? 40 : 34, height: isMobile ? 40 : 34, borderRadius: '50%', background: `radial-gradient(circle at 35% 35%, ${CHIP_COLORS[i]}cc, ${CHIP_COLORS[i]}88)`, border: selectedChip === v ? '2px solid white' : '2px solid rgba(255,255,255,0.2)', color: 'white', fontSize: isMobile ? 11 : 10, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                      {v >= 1000 ? `${v/1000}k` : v}
                    </button>
                  ))}
                </div>
                <div style={{ padding: '4px 10px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 6, color: '#c9a84c', fontWeight: 700, minWidth: 60, textAlign: 'center' }}>
                  {stake > 0 ? fmtCoins(stake) : '—'}
                </div>
                {stake > 0 && <button onClick={() => setStake(0)} style={{ padding: isMobile ? '8px 10px' : '4px 8px', background: 'rgba(220,38,38,0.2)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 4, color: '#f87171', fontSize: 11, cursor: 'pointer' }}>CLR</button>}
                <button onClick={confirmBet} disabled={placing || stake === 0}
                  style={{ padding: isMobile ? '10px 18px' : '6px 16px', background: stake > 0 ? '#16a34a' : '#1e293b', border: 'none', borderRadius: 6, color: 'white', fontSize: 12, fontWeight: 700, cursor: stake > 0 ? 'pointer' : 'default', whiteSpace: 'nowrap', opacity: placing ? 0.6 : 1, flex: isMobile ? 1 : undefined }}>
                  {placing ? 'Placing…' : 'Place Bet'}
                </button>
              </div>
            )}

            {toast && <div style={{ padding: '6px 14px', background: '#166534', borderTop: '1px solid #15803d', fontSize: 12, color: '#86efac', animation: 'mac88SlideIn 0.2s ease-out' }}>{toast}</div>}
          </div>

          {/* Last result */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', gap: 10, background: '#0f172a', borderTop: '1px solid #1e293b', flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: '#475569', whiteSpace: 'nowrap', fontWeight: 600 }}>Last Result</span>
            <div style={{ display: 'flex', gap: 5, flex: 1, minWidth: 0, overflowX: 'auto' }}>
              {results.length === 0 && <span style={{ fontSize: 11, color: '#334155' }}>No results yet</span>}
              {results.map((r, i) => (
                <div key={i} style={{ width: 26, height: 26, borderRadius: '50%', background: r === 'A' ? '#c84b31' : r === 'B' ? '#1d6b35' : '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: 'white', border: '2px solid rgba(255,255,255,0.15)', flexShrink: 0 }}>{r}</div>
              ))}
            </div>
            {isMobile
              ? <span onClick={() => setShowMyBets(v => !v)} style={{ fontSize: 11, color: '#3b82f6', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 }}>My Bets {myBets.length > 0 && `(${myBets.length})`} {showMyBets ? '▲' : '▼'}</span>
              : <span style={{ fontSize: 11, color: '#3b82f6', cursor: 'pointer', whiteSpace: 'nowrap' }}>View All</span>
            }
          </div>

          {/* Mobile: collapsible My Bets */}
          {isMobile && showMyBets && (
            <div style={{ background: '#0f172a', borderTop: '1px solid #1e293b', flexShrink: 0, animation: 'mac88SlideIn 0.2s ease-out' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 4, padding: '5px 10px', borderBottom: '1px solid #1e293b', fontSize: 10, color: '#475569', fontWeight: 600 }}>
                <span>Matched Bet</span><span>Odds</span><span>Stake</span>
              </div>
              {myBetsList}
            </div>
          )}
        </div>

        {/* Right panel — desktop only */}
        {!isMobile && (
          <div style={{ width: 200, flexShrink: 0, display: 'flex', flexDirection: 'column', borderLeft: '1px solid #1e293b', background: '#0f172a' }}>
            <div style={{ padding: '8px 12px', fontWeight: 700, fontSize: 13, borderBottom: '1px solid #1e293b', background: 'linear-gradient(180deg, #1e293b, #0f172a)' }}>My Bet</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 4, padding: '5px 10px', borderBottom: '1px solid #1e293b', fontSize: 10, color: '#475569', fontWeight: 600 }}>
              <span>Matched Bet</span><span>Odds</span><span>Stake</span>
            </div>
            {myBetsList}
            <div style={{ padding: '8px 12px', borderTop: '1px solid #1e293b', background: 'rgba(255,255,255,0.02)' }}>
              <div style={{ fontSize: 10, color: '#475569' }}>Player</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>{displayName}</div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes mac88CardIn { from { opacity:0; transform:translateY(-12px) scale(0.9); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes mac88WinIn  { from { opacity:0; transform:scale(0.9); } to { opacity:1; transform:scale(1); } }
        @keyframes mac88SlideIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
      `}</style>
    </div>
  )
}